import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowLeftRight,
  Hotel,
  Utensils,
  Plane,
  Camera,
  Bus,
  StickyNote as StampIcon,
  Check,
  Minus,
} from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { SmartImage } from "@/components/SmartImage";
import { formatINR } from "@/data/tours";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const INCL = [
  { key: "hotel", label: "Hotel", Icon: Hotel },
  { key: "meals", label: "Meals", Icon: Utensils },
  { key: "flight", label: "Flight", Icon: Plane },
  { key: "sightseeing", label: "Sightseeing", Icon: Camera },
  { key: "transport", label: "Transport", Icon: Bus },
  { key: "visa", label: "Visa", Icon: StampIcon },
];

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[150px_1fr] border-b border-gray-100">
    <div className="bg-[var(--soft)] px-3 py-3 text-[12px] font-semibold text-[#0b2545]/70 flex items-center">{label}</div>
    <div className="grid" style={{ gridTemplateColumns: `repeat(${children.length}, minmax(0,1fr))` }}>
      {children}
    </div>
  </div>
);

const Cell = ({ children, className = "" }) => (
  <div className={`px-3 py-3 text-[13px] text-[var(--navy)] border-l border-gray-100 ${className}`}>{children}</div>
);

const CompareDialog = () => {
  const { items, open, setOpen, remove, clear } = useCompare();
  const navigate = useNavigate();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="compare-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100 flex-row items-center justify-between">
          <DialogTitle className="text-[var(--navy)] text-lg inline-flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-[var(--yellow-2)]" /> Compare Tours ({items.length})
          </DialogTitle>
          <button type="button" onClick={clear} data-testid="compare-clear-all" className="text-[13px] font-semibold text-[var(--red)] underline mr-6">Clear all</button>
        </DialogHeader>

        <div className="overflow-x-auto thin-scroll max-h-[70vh]">
          <div className="min-w-[540px]">
            {/* header row: images */}
            <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[150px_1fr] border-b border-gray-100">
              <div className="bg-[var(--soft)]" />
              <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}>
                {items.map((t) => (
                  <div key={t.slug} className="p-2 border-l border-gray-100 relative">
                    <button type="button" onClick={() => remove(t.slug)} data-testid={`compare-remove-${t.slug}`} className="absolute top-3 right-3 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 z-10">
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="rounded-lg overflow-hidden h-24">
                      <SmartImage src={t.image} alt={t.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-2 font-semibold text-[13px] text-[var(--navy)] leading-tight line-clamp-2">{t.title}</div>
                  </div>
                ))}
              </div>
            </div>

            <Row label="Price / person">
              {items.map((t) => <Cell key={t.slug} className="font-bold">{formatINR(t.startingPrice)}</Cell>)}
            </Row>
            <Row label="EMI / month">
              {items.map((t) => <Cell key={t.slug}>{formatINR(t.emiPerMonth)}</Cell>)}
            </Row>
            <Row label="Duration">
              {items.map((t) => <Cell key={t.slug}>{t.days} Days / {t.nights} Nights</Cell>)}
            </Row>
            <Row label="Covers">
              {items.map((t) => (
                <Cell key={t.slug}>
                  {t.countriesCount ? `${t.countriesCount} ${t.countriesCount > 1 ? "Countries" : "Country"} · ` : `${t.statesCount} States · `}
                  {t.citiesCount} Cities
                </Cell>
              ))}
            </Row>
            <Row label="Departures">
              {items.map((t) => <Cell key={t.slug}>{t.dates.length} dates</Cell>)}
            </Row>
            <Row label="Rating">
              {items.map((t) => <Cell key={t.slug}>{t.rating}★ · {t.reviewsCount} reviews</Cell>)}
            </Row>
            <Row label="Speciality">
              {items.map((t) => <Cell key={t.slug}>{t.speciality || "—"}</Cell>)}
            </Row>
            {INCL.map(({ key, label, Icon }) => {
              // only show a row if at least one tour has the concept defined
              const relevant = items.some((t) => t.inclusions[key] !== undefined);
              if (!relevant) return null;
              return (
                <Row key={key} label={label}>
                  {items.map((t) => {
                    const v = t.inclusions[key];
                    const on = v === true;
                    const optional = v === "optional";
                    return (
                      <Cell key={t.slug} className="text-center">
                        {optional ? (
                          <span className="text-[11px] text-[var(--yellow-2)] font-semibold">Optional</span>
                        ) : on ? (
                          <Check className="w-4 h-4 text-green-600 mx-auto" />
                        ) : (
                          <Minus className="w-4 h-4 text-gray-300 mx-auto" />
                        )}
                      </Cell>
                    );
                  })}
                </Row>
              );
            })}
            <Row label="Highlights">
              {items.map((t) => <Cell key={t.slug} className="text-[12px] text-[#0b2545]/70">{t.highlights.slice(0, 3).join(", ")}…</Cell>)}
            </Row>
            <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[150px_1fr]">
              <div className="bg-[var(--soft)]" />
              <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}>
                {items.map((t) => (
                  <div key={t.slug} className="px-3 py-3 border-l border-gray-100">
                    <Button
                      onClick={() => { setOpen(false); navigate(`/tour/${t.slug}`); }}
                      data-testid={`compare-view-${t.slug}`}
                      className="w-full bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold text-[12px] rounded-md"
                    >
                      View Details
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CompareBar = () => {
  const { items, remove, clear, setOpen } = useCompare();

  return (
    <>
      <AnimatePresence>
        {items.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-10px_30px_-15px_rgba(11,37,69,0.35)]"
            data-testid="compare-bar"
          >
            <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-3 flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 mr-1">
                <ArrowLeftRight className="w-5 h-5 text-[var(--yellow-2)]" />
                <span className="font-semibold text-[var(--navy)] text-[14px] whitespace-nowrap">Compare <span className="text-[var(--yellow-2)]">{items.length}</span></span>
              </div>
              <div className="flex-1 flex items-center gap-2 overflow-x-auto no-scrollbar">
                {items.map((t) => (
                  <div key={t.slug} className="relative shrink-0 flex items-center gap-2 bg-[var(--soft)] rounded-lg pl-1.5 pr-6 py-1.5">
                    <div className="w-10 h-8 rounded overflow-hidden shrink-0">
                      <SmartImage src={t.image} alt={t.title} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-[12.5px] font-medium text-[var(--navy)] max-w-[120px] truncate">{t.title}</span>
                    <button type="button" onClick={() => remove(t.slug)} data-testid={`compare-bar-remove-${t.slug}`} className="absolute top-1/2 -translate-y-1/2 right-1.5 text-gray-400 hover:text-[var(--red)]">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={clear} data-testid="compare-bar-clear" className="hidden sm:inline text-[13px] font-semibold text-gray-500 underline whitespace-nowrap">Clear</button>
              <Button
                onClick={() => setOpen(true)}
                disabled={items.length < 2}
                data-testid="compare-now-btn"
                className="bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white font-semibold rounded-md whitespace-nowrap disabled:opacity-50"
              >
                Compare {items.length < 2 ? "(add 2+)" : "Now"}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <CompareDialog />
    </>
  );
};
